import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowLeft, ChevronDown, ChevronUp, Shield, HelpCircle } from 'lucide-react';

/**
 * FAQ PAGES (BUILD 8)
 * 
 * Role-specific FAQ pages with:
 * - Calm, clear, direct tone
 * - Non-marketing, non-legal, non-threatening
 * - Organized by common questions
 */

const FAQ_DATA = {
  individuals: {
    title: 'FAQ for Individuals',
    subtitle: 'Questions about using LikeKind as an adult',
    color: 'var(--lk-primary-green)',
    sections: [
      {
        title: 'Getting Started',
        questions: [
          {
            q: 'What is LikeKind?',
            a: 'LikeKind is a calm space for people who want to connect with others through groups, activities, and shared interests — without the noise of traditional social platforms. There are no algorithms, no engagement tricks, and no selling your data.'
          },
          {
            q: 'Is this a social media app?',
            a: 'No. LikeKind is designed for real coordination and connection, not social media. There are no public feeds, no follower counts, no likes, and no comments. You control what you share and with whom.'
          },
          {
            q: 'What is Base Camp?',
            a: 'Base Camp is your private dashboard. It shows your groups, commitments, contacts, and activities. You decide what sections are visible to others.'
          }
        ]
      },
      {
        title: 'Privacy & Safety',
        questions: [
          {
            q: 'Who can see my information?',
            a: 'You control visibility for each section of Base Camp. By default, everything is private. You can choose to share specific sections with contacts, groups, or keep them completely private.'
          },
          {
            q: 'Does LikeKind sell my data?',
            a: 'No. See our Privacy Policy and Trust & Safety page for full details on data practices.'
          },
          {
            q: 'Can I delete my account?',
            a: 'Yes. You can delete your account at any time from Settings. This removes all your data from our systems.'
          }
        ]
      },
      {
        title: 'Groups & Commons',
        questions: [
          {
            q: 'What is the difference between Groups and Commons?',
            a: 'Groups are temporary — they expire after a set time (default 90 days). Commons are permanent community spaces managed by stewards, designed for ongoing communities like neighborhoods or organizations.'
          },
          {
            q: 'How do I join a group?',
            a: 'Browse local activities in Base Camp or use the Discover feature. When you find something interesting, you can join directly or express interest through Intent Cards.'
          }
        ]
      },
      {
        title: 'SCUTA',
        questions: [
          {
            q: 'What is SCUTA?',
            a: 'SCUTA is a separate timeline tool connected to LikeKind. It helps you see time clearly — historical events, personal timelines, and how events relate to each other. SCUTA is not a social feature.'
          },
          {
            q: 'Do I need a membership for SCUTA?',
            a: 'Basic SCUTA access is available to everyone. Personal timelines require a Personal Member or Founder membership.'
          }
        ]
      }
    ]
  },
  families: {
    title: 'FAQ for Families',
    subtitle: 'Questions about family accounts and child safety',
    color: 'var(--lk-secondary-orange)',
    sections: [
      {
        title: 'Account Types',
        questions: [
          {
            q: 'What account types exist for families?',
            a: 'Parent (18+): Full control over family. Child (under 13): Requires parent account, no discoverability, strict protections. Teen (13-15): Parent-controlled by default with gradual independence. Youth Adult (16-17): Can request adult mode with parent approval.'
          },
          {
            q: 'Can children create their own accounts?',
            a: 'No. Children can only exist within a family created by a parent. The parent account must be created first, and then children can be added under that family.'
          },
          {
            q: 'What happens when my child turns 18?',
            a: 'Youth adults (16-17) can request to convert to a full adult account. This requires explicit parent approval and a new email address. At 18, they can create their own independent account.'
          }
        ]
      },
      {
        title: 'Safety & Privacy',
        questions: [
          {
            q: 'Can strangers find my child?',
            a: 'No. Children under 13 have zero discoverability. They cannot be found through search. Teens have limited discoverability controlled by parent settings.'
          },
          {
            q: 'Can my child message other children?',
            a: 'No. Student-to-student messaging is disabled by default and cannot be enabled. See Trust & Safety for child protection details.'
          },
          {
            q: 'Can I see what my child does on LikeKind?',
            a: 'Yes. Parents have read-only visibility on all child content. Children always see when Parent Mode is ON.'
          },
          {
            q: 'What is Control Mode?',
            a: 'Control Mode determines how much oversight parents have. ON (default for children): Full parent control. SHARED: Both parent and child can see activity. OFF: Reduced oversight (only available for teens 13+).'
          }
        ]
      },
      {
        title: 'Family Calendar',
        questions: [
          {
            q: 'What is the Family Calendar?',
            a: 'The Family Calendar aggregates events from all family members into one view. Parents can create family events, and everyone sees what\'s happening.'
          },
          {
            q: 'Can children add events?',
            a: 'Children under 13 cannot add events. Teens may add events depending on parent settings, but parents can always see and manage all family events.'
          }
        ]
      },
      {
        title: 'Screen Time',
        questions: [
          {
            q: 'Does LikeKind enforce screen time limits?',
            a: 'No. LikeKind provides screen-time coordination tools, but they are non-enforcing. They help families coordinate and communicate about device usage, not restrict access.'
          },
          {
            q: 'What screen time features exist?',
            a: 'Quiet hours (recommended times without device use), time warnings (notifications after certain usage), and bonus time (parent-granted extra time). All are visible to both parent and child.'
          }
        ]
      }
    ]
  },
  schools: {
    title: 'FAQ for Schools & Institutions',
    subtitle: 'Questions about coordination, safety, and compliance',
    color: 'var(--lk-earth-brown)',
    sections: [
      {
        title: 'What is LikeKind for Schools?',
        questions: [
          {
            q: 'What is LikeKind for schools and institutions?',
            a: 'LikeKind provides a structured coordination space for schools, programs, and institutions to manage calendars, announcements, documents, and participation without advertising, tracking, or social mechanics.'
          },
          {
            q: 'Is LikeKind social media?',
            a: 'No. LikeKind does not use: feeds optimized for engagement, algorithms, advertising, behavioral tracking, or student-to-student discovery. It is a coordination and visibility platform, not a social network.'
          },
          {
            q: 'How do schools and institutions sign up?',
            a: 'They sign up directly through LikeKind\'s membership flow by selecting School or Institution. No personal account is required beforehand.'
          }
        ]
      },
      {
        title: 'What Schools & Institutions Receive',
        questions: [
          {
            q: 'What is created when a school signs up?',
            a: 'A school receives: a Learning Commons, teacher accounts, Student Notebooks (not profiles), school calendar, attachment tools (homework, notices, permission slips), and parent read-only visibility where applicable.'
          },
          {
            q: 'What is created when an institution signs up?',
            a: 'An institution receives: a Commons, steward-managed membership, public-facing informational profile, calendars and events, and policy-governed participation.'
          }
        ]
      },
      {
        title: 'Student Safety',
        questions: [
          {
            q: 'Can students message each other?',
            a: 'No. There is no student-to-student messaging.'
          },
          {
            q: 'Are minors discoverable?',
            a: 'No. Children and students are not searchable, discoverable, or contactable.'
          },
          {
            q: 'Are ads shown to students?',
            a: 'No. There are no ads, tracking pixels, embeds, or behavioral targeting anywhere in the platform.'
          }
        ]
      },
      {
        title: 'Student Notebooks',
        questions: [
          {
            q: 'What is a Student Notebook?',
            a: 'A Student Notebook is a utility workspace for school-related communication and attachments. It is not a profile and has no discovery or peer messaging.'
          },
          {
            q: 'What can be sent to Student Notebooks?',
            a: 'Teachers can send: homework assignments, class notes, announcements, permission slips, report cards, field trip information, and PTA communications.'
          },
          {
            q: 'Can students reply to teachers?',
            a: 'Students can acknowledge receipt and reply to specific messages, but all replies are visible to parents and school stewards.'
          }
        ]
      },
      {
        title: 'Enforcement & Compliance',
        questions: [
          {
            q: 'What happens if rules are violated?',
            a: 'See Trust & Safety for enforcement stages. Decisions are final.'
          },
          {
            q: 'Does student safety cost money?',
            a: 'No. Safety features are always free. Student protection is never paywalled.'
          }
        ]
      }
    ]
  },
  institutions: {
    title: 'FAQ for Institutions',
    subtitle: 'Questions about nonprofits, organizations, and institutions',
    color: 'var(--lk-earth-brown)',
    sections: [
      {
        title: 'Institution Types',
        questions: [
          {
            q: 'What institution types are supported?',
            a: 'Schools (private, homeschool, micro-school), Educational Programs (after-school, tutoring, co-op), Nonprofits and Community Organizations, and General Institutions.'
          },
          {
            q: 'Is this a social media profile for my organization?',
            a: 'No. Institution profiles are professional presence pages — not social profiles. There are no feeds, no likes, no comments. Just information, calendars, and coordination tools.'
          }
        ]
      },
      {
        title: 'Purpose & Environment',
        questions: [
          {
            q: 'What does LikeKind provide?',
            a: 'A coordination space for people, time, and information. A non-extractive alternative to social platforms and ad-based tools.'
          },
          {
            q: 'What does LikeKind NOT allow?',
            a: 'Advertising, tracking, algorithms, student discovery, and promotion outside policy are not permitted.'
          }
        ]
      },
      {
        title: 'Stewardship',
        questions: [
          {
            q: 'What is a Steward?',
            a: 'Stewards are responsible for managing the institution. Every institution requires both a Primary Steward and an Assistant Steward. Critical actions need dual approval.'
          },
          {
            q: 'Why is dual-steward required?',
            a: 'Dual-steward requirement prevents any single person from having unchecked control. This protects the institution and its members from abuse of power.'
          },
          {
            q: 'What actions require dual approval?',
            a: 'Deleting the institution, removing stewards, changing visibility settings, bulk actions on member data, and changing safety settings.'
          }
        ]
      },
      {
        title: 'Data & Privacy',
        questions: [
          {
            q: 'What is Public vs Private visibility?',
            a: 'Public institutions appear in Discover and can be found through search. Private institutions are invitation-only and cannot be searched.'
          },
          {
            q: 'Does LikeKind sell or share data?',
            a: 'No. See Privacy Policy and Trust & Safety for data practices.'
          }
        ]
      },
      {
        title: 'Enforcement',
        questions: [
          {
            q: 'What enforcement applies to institutions?',
            a: 'See Trust & Safety for enforcement stages. Violations may result in suspension or removal without appeal.'
          },
          {
            q: 'What about public discovery?',
            a: 'Institutions may appear on public discovery pages for informational purposes only. See Content Guidelines for promotional restrictions.'
          }
        ]
      }
    ]
  },
  commons: {
    title: 'FAQ for Commons',
    subtitle: 'Questions about community spaces and stewardship',
    color: 'var(--lk-earth-brown)',
    sections: [
      {
        title: 'What is a Commons?',
        questions: [
          {
            q: 'What is a Commons?',
            a: 'A Commons is a permanent community space on LikeKind — for neighborhoods, organizations, institutions, and groups who want to coordinate activities, share resources, and stay connected.'
          },
          {
            q: 'How is a Commons different from a Group?',
            a: 'Groups are temporary — they expire after a set time (default 90 days). Commons are permanent community spaces managed by stewards, designed for ongoing communities.'
          },
          {
            q: 'Who can create a Commons?',
            a: 'Any adult can create a Commons. However, every Commons requires at least two stewards to be fully active.'
          }
        ]
      },
      {
        title: 'Stewardship',
        questions: [
          {
            q: 'What is a Steward?',
            a: 'Stewards manage the Commons. Every Commons requires at least two stewards for critical actions. See Trust & Safety for stewardship details.'
          },
          {
            q: 'Why are two stewards required?',
            a: 'Dual-steward requirement prevents any single person from having unchecked control.'
          },
          {
            q: 'What actions require dual approval?',
            a: 'Deleting the Commons, removing stewards, changing visibility settings, and modifying safety rules.'
          }
        ]
      },
      {
        title: 'Privacy & Safety',
        questions: [
          {
            q: 'Who can see my Commons?',
            a: 'You choose: Public (discoverable in search), Members-only (visible only to members), or Invitation-only (not searchable, requires invite code).'
          },
          {
            q: 'Are participant names public?',
            a: 'No. By default, only participant counts are shown for events. Names are visible only to organizers and stewards.'
          },
          {
            q: 'What if children participate in Commons activities?',
            a: 'If your Commons serves children, student safety rules automatically apply. Children cannot be discovered, and parent visibility is enabled.'
          }
        ]
      },
      {
        title: 'Calendar & Scheduling',
        questions: [
          {
            q: 'How does the Commons calendar work?',
            a: 'The Commons calendar shows all scheduled activities. Members can RSVP to events. Stewards can create and manage events.'
          },
          {
            q: 'Can I export the calendar?',
            a: 'Yes. LikeKind supports ICS export for Google Calendar, Apple Calendar, and Outlook.'
          },
          {
            q: 'Are there engagement notifications?',
            a: 'No engagement tricks. Notifications are limited to: event starting soon, event updated, and event canceled. No push notifications designed to pull you back in.'
          }
        ]
      }
    ]
  },
  business: {
    title: 'FAQ for Businesses',
    subtitle: 'Questions about Business membership and sponsorship',
    color: '#6B7280',
    sections: [
      {
        title: 'Business Membership',
        questions: [
          {
            q: 'What is a Business membership?',
            a: 'Business membership provides eligibility to apply for sponsorship placements, the ability to represent a real business identity, and access to Commons stewardship where applicable. It is a foundation for honest presence — not an advertising account.'
          },
          {
            q: 'Does Business membership include advertising?',
            a: 'No. LikeKind does not offer advertising. Business membership provides eligibility to apply for vetted sponsorship placements, which are separate, manual, and subject to approval. There are no ads, no targeting, no engagement metrics.'
          },
          {
            q: 'What is the difference between membership and sponsorship?',
            a: 'Membership is access — it allows you to be present on LikeKind. Sponsorship is placement — a separate, manually vetted opportunity to appear in designated areas. Membership does not guarantee sponsorship approval.'
          },
          {
            q: 'Can I use Business membership for personal use?',
            a: 'No. Business membership is for verified businesses only. Personal accounts should use Individual or Family membership.'
          }
        ]
      },
      {
        title: 'Groups & Promotional Content (IMPORTANT)',
        questions: [
          {
            q: 'Can my business join groups?',
            a: 'Yes. Businesses may join groups. However, businesses may NOT bypass group policy. Each group has a promotional content policy set by its steward at creation time.'
          },
          {
            q: 'What is a group\'s promotional content policy?',
            a: 'Every group has one of three promotional policies: (1) No promotional content allowed (default), (2) Promotional content allowed with steward approval, or (3) Promotional content allowed and clearly marked. This policy is locked at group creation and cannot be changed.'
          },
          {
            q: 'What counts as promotional content?',
            a: 'Promotional content includes: business advertising, sales links, paid services, external monetized content, and self-promotion beyond the group\'s stated purpose.'
          },
          {
            q: 'What happens if I post promotional content in a no-promotion group?',
            a: 'Business accounts posting promotional content in groups where promotion is not allowed will receive: content removal, loss of posting privileges in that group, and the violation will be logged. Repeated violations escalate per platform enforcement stages.'
          },
          {
            q: 'Can I request a group change its promotional policy?',
            a: 'No. Group promotional policies are locked at creation time by the steward. They cannot be changed afterward. If you need a space for promotional content, find a group that allows it or create your own.'
          },
          {
            q: 'Where can I see a group\'s policy?',
            a: 'Every group has a Group Profile page that displays the group\'s promotional policy, stewards, and full Group Policy. The policy is visible before you join.'
          }
        ]
      },
      {
        title: 'Sponsorships',
        questions: [
          {
            q: 'How are sponsors selected?',
            a: 'Sponsors are selected through manual, human review. Every placement must pass a checklist covering: category alignment, content accuracy, no external embeds, no misleading claims, no child exposure risk, and no political manipulation. There is no algorithmic selection or bidding.'
          },
          {
            q: 'Can my business appear in categories?',
            a: 'If you have Business membership, you may apply for category sponsorship placements. Approval is not guaranteed and requires manual review. Each category can have only one sponsor at a time.'
          },
          {
            q: 'What can I include in a sponsorship placement?',
            a: 'Sponsorship placements may include: a title (max 100 characters), a description (max 240 characters), one static image, and one link (internal or external). No video, no scripts, no tracking pixels, no embeds.'
          },
          {
            q: 'Can sponsors change content after approval?',
            a: 'No. Any change to approved content requires complete re-submission and re-approval. Content is immutable once live. This protects users from bait-and-switch tactics.'
          },
          {
            q: 'How long do sponsorship placements last?',
            a: 'Sponsorship placements are approved for 30-day cycles. At the end of each cycle, re-review is required. There is no automatic renewal.'
          }
        ]
      },
      {
        title: 'What We Don\'t Offer',
        questions: [
          {
            q: 'Can businesses target users?',
            a: 'No. LikeKind does not offer any form of targeting — behavioral, demographic, geographic, or interest-based. Sponsorship placements appear in designated areas only. No user data is shared with sponsors.'
          },
          {
            q: 'Why don\'t you offer analytics or impressions?',
            a: 'Analytics and impression counts create incentives for engagement manipulation. Sponsors receive only: placement duration, placement category, and billing status. No view counts. No click rates. No conversion tracking.'
          },
          {
            q: 'Can I track user behavior from my placement?',
            a: 'No. External tracking pixels, scripts, and embeds are prohibited. If external links are used, they open in new tabs with no referrer data. LikeKind does not share any user data with sponsors.'
          },
          {
            q: 'Is there a dashboard for campaign performance?',
            a: 'No. There are no ad dashboards, CTR metrics, performance charts, engagement scoring, or optimization suggestions. Sponsors are guests, not data partners.'
          }
        ]
      },
      {
        title: 'Violations & Enforcement',
        questions: [
          {
            q: 'What happens if a sponsor violates rules?',
            a: 'See Trust & Safety for 3-stage enforcement model. Child safety violations result in immediate permanent ban.'
          },
          {
            q: 'Are there appeals for violations?',
            a: 'See Trust & Safety for appeal process. Child-safety violations have no appeal.'
          }
        ]
      },
      {
        title: 'Safety',
        questions: [
          {
            q: 'Why is this safer for families and schools?',
            a: 'No targeting, no tracking, no algorithmic manipulation. Children are never exposed to behavioral advertising. See Trust & Safety for full child protection details.'
          },
          {
            q: 'Can sponsors target children?',
            a: 'No. Child-directed content is prohibited. See Trust & Safety for enforcement.'
          }
        ]
      }
    ]
  },
  scuta: {
    title: 'FAQ for SCUTA',
    subtitle: 'Questions about the timeline instrument',
    color: '#C2A45E',
    sections: [
      {
        title: 'What is SCUTA?',
        questions: [
          {
            q: 'What is SCUTA?',
            a: 'SCUTA is a timeline instrument. It helps people see time clearly — not as opinion, not as noise, not as engagement — but as sequence, pattern, and consequence.'
          },
          {
            q: 'Is SCUTA part of LikeKind?',
            a: 'SCUTA is connected to LikeKind but has its own independent identity. You can view public timelines without a LikeKind account.'
          },
          {
            q: 'Do I need to log in to use SCUTA?',
            a: 'No. Public historical timelines are viewable without login. Personal timelines require a LikeKind membership.'
          }
        ]
      },
      {
        title: 'Timelines',
        questions: [
          {
            q: 'What timelines are available?',
            a: 'SCUTA includes: Canonical History, Religion History, Conflict History, Unresolved History, Timekeeping & Calendars, and more. Personal timelines are available for members.'
          },
          {
            q: 'How are events placed on timelines?',
            a: 'Events are placed based on documented dates and sources. SCUTA does not rank or manipulate the order. Sources are shown, not hidden.'
          },
          {
            q: 'Can I add my own events?',
            a: 'Personal timeline members can add events to their own timelines. Public historical timelines are curated by SCUTA editors.'
          }
        ]
      },
      {
        title: 'Design & Philosophy',
        questions: [
          {
            q: 'Why is SCUTA designed this way?',
            a: 'SCUTA has a museum/archive aesthetic because it is an instrument for study, not entertainment. The design encourages careful observation, not quick scrolling.'
          },
          {
            q: 'Why no comments or reactions?',
            a: 'SCUTA is not a social platform. Comments and reactions encourage engagement over observation. SCUTA presents history for study, not argument.'
          },
          {
            q: 'What are the Rules of Use?',
            a: 'Observe first. Compare carefully. Do not assume intent. Do not treat sequence as judgment. Context matters. Silence is part of the design.'
          }
        ]
      }
    ]
  }
};

export default function FAQPage() {
  const navigate = useNavigate();
  const { audience } = useParams();
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});

  const faqData = FAQ_DATA[audience] || FAQ_DATA.individuals;

  const toggleSection = (sectionIdx) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionIdx]: !prev[sectionIdx]
    }));
  };

  const toggleQuestion = (sectionIdx, questionIdx) => {
    const key = `${sectionIdx}-${questionIdx}`;
    setExpandedQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-black/5">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-primary)' }} />
            </button>
            <div className="flex-1 text-center">
              <h1 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>FAQ</h1>
            </div>
            <div className="w-9" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: `${faqData.color}20` }}
            >
              <HelpCircle className="w-5 h-5" style={{ color: faqData.color }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
                {faqData.title}
              </h1>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>{faqData.subtitle}</p>
            </div>
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-4">
          {faqData.sections.map((section, sectionIdx) => (
            <Card 
              key={sectionIdx}
              className="overflow-hidden"
              style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
            >
              <button
                onClick={() => toggleSection(sectionIdx)}
                className="w-full p-4 flex items-center justify-between text-left"
              >
                <h2 className="font-semibold" style={{ color: faqData.color }}>{section.title}</h2>
                {expandedSections[sectionIdx] !== false ? (
                  <ChevronUp className="w-5 h-5" style={{ color: 'var(--fy-text-disabled)' }} />
                ) : (
                  <ChevronDown className="w-5 h-5" style={{ color: 'var(--fy-text-disabled)' }} />
                )}
              </button>
              
              {expandedSections[sectionIdx] !== false && (
                <div className="border-t" style={{ borderColor: 'var(--fy-border-subtle)' }}>
                  {section.questions.map((item, questionIdx) => {
                    const isExpanded = expandedQuestions[`${sectionIdx}-${questionIdx}`];
                    return (
                      <div 
                        key={questionIdx}
                        className="border-b last:border-b-0"
                        style={{ borderColor: 'var(--fy-border-subtle)' }}
                      >
                        <button
                          onClick={() => toggleQuestion(sectionIdx, questionIdx)}
                          className="w-full px-4 py-3 flex items-start justify-between text-left gap-4"
                        >
                          <span className="font-medium text-sm" style={{ color: 'var(--fy-text-primary)' }}>
                            {item.q}
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--fy-text-disabled)' }} />
                          ) : (
                            <ChevronDown className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--fy-text-disabled)' }} />
                          )}
                        </button>
                        {isExpanded && (
                          <div className="px-4 pb-4">
                            <p className="text-sm leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
                              {item.a}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Contact Support */}
        <Card 
          className="mt-8 p-6 text-center"
          style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
        >
          <Shield className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
          <h3 className="font-semibold mb-1" style={{ color: 'var(--fy-text-primary)' }}>Still have questions?</h3>
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Review our policy pages or contact support.
          </p>
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            <Button 
              onClick={() => navigate('/trust-safety')}
              variant="outline"
              size="sm"
              className="rounded-full"
              style={{ borderColor: 'var(--fy-border-subtle)' }}
            >
              Trust & Safety
            </Button>
            <Button 
              onClick={() => navigate('/content-guidelines')}
              variant="outline"
              size="sm"
              className="rounded-full"
              style={{ borderColor: 'var(--fy-border-subtle)' }}
            >
              Content Guidelines
            </Button>
            <Button 
              onClick={() => navigate('/sponsor-policy')}
              variant="outline"
              size="sm"
              className="rounded-full"
              style={{ borderColor: 'var(--fy-border-subtle)' }}
            >
              Sponsor Policy
            </Button>
            <Button 
              onClick={() => navigate('/privacy')}
              variant="outline"
              size="sm"
              className="rounded-full"
              style={{ borderColor: 'var(--fy-border-subtle)' }}
            >
              Privacy Policy
            </Button>
          </div>
          <Button 
            onClick={() => navigate('/contact')}
            variant="outline"
            className="rounded-full"
            style={{ borderColor: 'var(--fy-border-subtle)' }}
          >
            Contact Support
          </Button>
        </Card>

        {/* Other FAQs */}
        <div className="mt-8">
          <h3 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Other FAQs</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(FAQ_DATA).filter(([key]) => key !== audience).map(([key, data]) => (
              <Button
                key={key}
                onClick={() => navigate(`/faq/${key}`)}
                variant="outline"
                size="sm"
                className="rounded-full capitalize"
                style={{ borderColor: data.color, color: data.color }}
              >
                {key}
              </Button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
